import { select, put, all } from "redux-saga/effects";
import { cloneDeep } from "lodash";

import makeRequest from "rdx/utils/makeRequest";
import getErrorActions from "rdx/utils/getErrorActions";
import actions from "rdx/actions";
import { isNullish } from "types/guards";
import selectors from "rdx/selectors";
import { orgProjectsApi } from "rdx/modules/enterpriseProjects/endpoints";
import { ORG_PROJECTS_TAGS } from "rdx/modules/enterpriseProjects/tags";
import { leadsApi } from "rdx/modules/leads/endpoints";
import { LEADS_TAGS } from "rdx/modules/leads/tags";
import { PROJECT_TAGS } from "rdx/modules/projects/tags";
import { keysToSnakeCase, camelizeKeys, formatPreSalesLead } from "lib/helpers";
import messages from "../messages";

function* createNewLead(action) {
  const {
    lead,
    address,
    enterprise,
    siteInfo,
    customSuccessMessage,
    confirm = false,
    confirmServiceArea = false,
  } = action.payload;
  const body = {
    lead: keysToSnakeCase(lead),
    address: keysToSnakeCase(address),
    enterprise,
    ...(siteInfo && { site_info: keysToSnakeCase(siteInfo) }),
    confirm,
    confirm_service_area: confirmServiceArea,
  };
  const { success, data, error } = yield* makeRequest.post("/leads", body);

  // The API warns rather than errors for an address outside the service areas, and saves nothing
  // until the Seller confirms.
  if (success && data?.warning) {
    yield put(actions.newMessageEvent({ message: messages.SERVICE_AREA_NOT_SERVICED, warning: data.warning.message }));
  } else if (success && data) {
    const leadDetails = camelizeKeys(data.body);
    if (data.status === 208) {
      yield all([
        put(actions.setDuplicateLeadDetails(leadDetails)),
        put(
          actions.newSuccessEvent({
            message: messages.DUPLICATE_LEAD_FOUND,
            payload: { ...leadDetails },
          }),
        ),
      ]);
    } else {
      const { projects, paging } = yield select(selectors.getProjectPreSales);
      const presalesPaging = {
        ...paging,
        itemCount: isNullish(paging.itemCount) ? null : paging.itemCount + 1,
      };
      const presalesOut = cloneDeep(projects);
      const preSalesLead = formatPreSalesLead({ ...leadDetails });
      presalesOut.unshift(preSalesLead);

      yield all([
        put(actions.setLeadToProposalDrawerLeadId({ id: leadDetails.id })),
        put(actions.setLeadToProposalDrawerLeadDetails(leadDetails)),
        put(actions.setLeadToProposalDrawerProjectDetails(leadDetails.providerLeads[0])),
        put(
          actions.setProjectsPreSales({
            projects: presalesOut,
            paging: presalesPaging,
          }),
        ),
        put(
          actions.newSuccessEvent({
            message: customSuccessMessage || messages.LEAD_CREATE_SUCCESS,
            payload: { ...data.body },
          }),
        ),
        yield put(orgProjectsApi.util.invalidateTags([ORG_PROJECTS_TAGS.ORG_PRE_SALES_PROJECTS])),
        put(leadsApi.util.invalidateTags([LEADS_TAGS.LEADS_TABLE, PROJECT_TAGS.PRE_SALES_KPIS])),
      ]);
    }
  } else if (error) {
    if (error.message.includes("Do Not Call")) {
      return getErrorActions({ error, message: messages.ERROR_UPDATING_LEAD_DNC_PHONE });
    }
    return getErrorActions({ error, message: messages.ERROR_CREATING_LEAD });
  }
  return null;
}

export default createNewLead;
